import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TWidget } from 'widgets/Widget';

/**
 * @description Recommendations Wrapper widget
 * @param ProcessButton Base widget for extending
 * @returns Recommendations Wrapper class
 */
function RecommendationsWrapperClassCreator(Widget: TWidget) {
    class RecommendationsWrapper extends Widget {
        prefs() {
            return {
                analytics: null as { [key: string]: unknown } | null,
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         */
        init() {
            if (!this.prefs().analytics) {
                return;
            }

            this.eventBus().on('tile.product.link.click', 'onProductLinkClick');
        }

        /**
         * @description Executes when user clicks on product details link on product tile.
         * Usually used by analytics etc.
         * @listens tile.product.link.click
         * @param link - product link
         */
        onProductLinkClick(link: TRefElementInstance) {
            const linkElement = link.get();

            if (!linkElement || !this.ref('self').get()?.contains(linkElement)) {
                return;
            }

            const productAnalytics: Record<string, unknown> | undefined = link.data('analytics');

            if (!productAnalytics) {
                return;
            }

            const analyticsIndex = linkElement.closest('[data-analytics-index]');

            if (analyticsIndex) {
                let index = analyticsIndex.getAttribute('data-analytics-index');

                if (!index) {
                    // for mustache templates, index is not set
                    // so we calculate it based on the position of the element in its parent
                    // this is a workaround for mustache templates where index is not available
                    index = String(Array.from(analyticsIndex.parentElement?.children || []).indexOf(analyticsIndex) + 1);
                }

                productAnalytics.index = index;
                productAnalytics.itemsLength = analyticsIndex.parentElement?.childElementCount ?? 0;
            }

            this.pushProductAnalytics(productAnalytics as Record<string, unknown> | null);
        }

        /**
         * @description Executes when user clicks on add product on cross widget.
         * Usually used by analytics etc.
         * @listens productadded
         * @param widget - productDetailCrossSell
         */
        onProductAdded(widget) {
            if (!widget) {
                return;
            }

            const productAnalytics: Record<string, unknown> | undefined = widget.data('analytics');

            if (!productAnalytics) {
                return;
            }

            // Cross-sell products are shown one by one, so index is always 1
            productAnalytics.index = 1;
            productAnalytics.itemsLength = 1;

            this.pushProductAnalytics(productAnalytics as Record<string, unknown> | null);
        }

        /**
         * @description Pushes product analytics data to the event bus
         * @param productAnalytics - product analytics data
         */
        pushProductAnalytics(productAnalytics: Record<string, unknown> | null) {
            const analytics = this.prefs().analytics;

            if (!analytics || typeof analytics.event_name !== 'string' || !productAnalytics) {
                return;
            }

            analytics[analytics.event_name] = {
                ...(analytics[analytics.event_name] || {}),
                reco_item_id: productAnalytics.item_id,
                position: `${productAnalytics.index}_of_${productAnalytics.itemsLength}`
            };

            this.eventBus().emit('gtm.event', analytics);
        }
    }

    return RecommendationsWrapper;
}

export type TRecommendationsWrapper = ReturnType<typeof RecommendationsWrapperClassCreator>;

export type TRecommendationsWrapperInstance = InstanceType<TRecommendationsWrapper>;

export default RecommendationsWrapperClassCreator;
